import {
  Box,
  Chip,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  useTheme,
  Link,
  SlotProps,
  TypographyProps,
  ListItemTextOwnerState,
} from '@mui/material';
import { MapEntityDrawerContext } from 'features/mapping/components/MapEntityDrawer';
import { OverstoryLayerFeatureEntity } from 'features/mapping/mapping.types';
import { ElementType, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { DrawerStickyHeader } from 'components/DrawerStickyHeader';
import { MapEntityDrawerHeader } from 'features/mapping/components/MapEntityDrawerHeader';
import { InlineStatus } from 'components/InlineStatus';
import { getResponsiveFontSize } from 'theme/watchdutyTheme.utils';
import LaunchIcon from '@mui/icons-material/Launch';
import { useExternalFeatureQuery } from './useExternalFeatureQuery';

type OverstoryAttributes = {
  spanId: string;
  encroachmentRiskRating: string;
  level_1?: string;
  level_2?: string;
  pole_1?: string;
  pole_2?: string;
  pole_1SapId?: string;
  pole_2SapId?: string;
  spanUrlToOverstory: string;
};

type DetailListItemProps = {
  label: string;
  value: string;
  secondaryProps?: SlotProps<
    ElementType<TypographyProps, keyof JSX.IntrinsicElements>,
    object,
    ListItemTextOwnerState
  >;
};

const DetailListItem = (props: DetailListItemProps): JSX.Element => {
  const { label, value, secondaryProps } = props;
  const theme = useTheme();

  return (
    <ListItem divider>
      <ListItemText
        primary={label}
        secondary={value}
        slotProps={{
          primary: {
            color: 'secondary',
            variant: 'h4',
            sx: {
              fontWeight: 'fontWeightBold',
              fontSize: getResponsiveFontSize(theme.typography.pxToRem(18)),
            },
          },
          secondary: {
            component: 'div',
            color: 'textPrimary',
            fontWeight: 'fontWeightMedium',
            ...secondaryProps,
          },
        }}
      />
    </ListItem>
  );
};

export const OverStoryDrawerContent = (
  props: OverstoryLayerFeatureEntity,
): JSX.Element => {
  const { sourceId, featureId } = props;
  const { scrollElementRef } = useContext(MapEntityDrawerContext);
  const { t } = useTranslation();
  const { externalFeature, isLoading, isError } =
    useExternalFeatureQuery<OverstoryAttributes>({
      sourceId,
      featureId,
    });

  const attrs = externalFeature?.attributes;

  return (
    <Box>
      <DrawerStickyHeader
        scrollContainer={scrollElementRef?.current as HTMLElement}
      >
        <DrawerStickyHeader.ContentContainer>
          <MapEntityDrawerHeader>
            <Typography
              component="div"
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <Typography component="span" noWrap>
                {t('overstory.title')}
              </Typography>
              <Chip
                label={t('map.layers.private')}
                variant="watchduty"
                color="error"
                sx={{ marginLeft: 1 }}
                component="span"
              />
            </Typography>
          </MapEntityDrawerHeader>
        </DrawerStickyHeader.ContentContainer>
      </DrawerStickyHeader>

      {(isLoading || isError || !externalFeature) && (
        <InlineStatus status={isLoading ? 'loading' : 'error'} />
      )}

      {externalFeature && attrs && (
        <List>
          <DetailListItem
            label={t('overstory.span')}
            value={`Span ${attrs.spanId}`}
          />

          {attrs.level_1 && (
            <DetailListItem
              label={
                attrs.level_2 ? t('overstory.district') : t('overstory.circuit')
              }
              value={attrs.level_1}
            />
          )}

          {attrs.level_1 && attrs.level_2 && (
            <DetailListItem
              label={t('overstory.circuit')}
              value={attrs.level_2}
            />
          )}

          {attrs.pole_1 && !attrs.pole_1SapId && (
            <DetailListItem label={t('overstory.pole1')} value={attrs.pole_1} />
          )}

          {attrs.pole_2 && !attrs.pole_2SapId && (
            <DetailListItem label={t('overstory.pole2')} value={attrs.pole_2} />
          )}

          {attrs.pole_1SapId && (
            <DetailListItem
              label={t('overstory.pole1Sap')}
              value={attrs.pole_1SapId}
            />
          )}

          {attrs.pole_2SapId && (
            <DetailListItem
              label={t('overstory.pole2Sap')}
              value={attrs.pole_2SapId}
            />
          )}

          <DetailListItem
            label={t('overstory.vegetationEncroachment')}
            value={attrs.encroachmentRiskRating}
            secondaryProps={{ textTransform: 'capitalize' }}
          />

          <ListItemButton
            divider
            component={Link}
            href={attrs.spanUrlToOverstory}
            target="_blank"
          >
            <ListItemText primary={t('overstory.viewInOverstory')} />
            <LaunchIcon />
          </ListItemButton>
        </List>
      )}
    </Box>
  );
};
